/**
 * QuickActionsSection — admin editor for preset move/swap batches, and where
 * they are applied.
 *
 * These are the same presets the Quick Actions tab of the config workbook
 * holds; editing here or there is interchangeable, both write the
 * `quick_actions` admin config key. There is no button for this on the roster
 * page itself — Settings is the one place a preset is defined AND run, so
 * there is exactly one source of truth for what a preset does.
 *
 * Counters and times are dropdowns sourced from the live template metadata and
 * the fixed slot grid, so a preset cannot name a counter that doesn't exist or a
 * time that isn't on a 15-minute boundary.
 *
 * Applying one only makes sense against a roster of the same mode/shift the
 * preset was built for — the counter numbers a departure-night preset names
 * mean something else entirely on an arrival-morning roster — so "Apply" is
 * only offered when a roster is generated and its mode/shift matches.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { apiClient } from "@/api/client";
import { useNotification } from "@/contexts/NotificationContext";
import { generateTimeSlots, isValidTimeRange } from "@/config/modeConfig";
import { SWAP_MOVE_COLORS } from "@/config/swapMoveColors";
import type {
  ConflictChoice,
  CounterDefinition,
  CounterOp,
  CounterOpConflict,
  OperationMode,
  QuickActionPair,
  QuickActionPreset,
  ShiftType,
} from "@/types/api";

const MODES: OperationMode[] = ["arrival", "departure"];
const SHIFTS: ShiftType[] = ["morning", "night"];

const selStyle: React.CSSProperties = {
  padding: "4px 5px",
  border: "1px solid #d1d5db",
  borderRadius: 4,
  fontSize: 12,
  background: "white",
};

const iconBtn = (color: string): React.CSSProperties => ({
  padding: "3px 8px",
  background: "none",
  border: `1px solid ${color}`,
  borderRadius: 4,
  color,
  cursor: "pointer",
  fontSize: 11,
  lineHeight: 1.6,
  flexShrink: 0,
});

/** Problems that would make a preset unusable on the roster, checked as you type. */
function presetProblems(preset: QuickActionPreset, counters: string[]): string[] {
  const out: string[] = [];
  if (!preset.name.trim()) out.push("Give the preset a name.");
  if (preset.pairs.length === 0) out.push("Add at least one counter pair.");

  preset.pairs.forEach((pair, i) => {
    const where = `Pair ${i + 1}`;
    if (pair.from && counters.length && !counters.includes(pair.from)) {
      out.push(`${where}: ${pair.from} is not a ${preset.mode} counter.`);
    }
    if (pair.to && counters.length && !counters.includes(pair.to)) {
      out.push(`${where}: ${pair.to} is not a ${preset.mode} counter.`);
    }
    if (pair.from && pair.from === pair.to) out.push(`${where}: From and To are the same counter.`);
    if (!isValidTimeRange(pair.start, pair.end, preset.shift)) {
      out.push(`${where}: end time must be after start time.`);
    }
  });

  // Two pairs writing the same counter over overlapping times have no defined
  // outcome (unlike a busy destination, which is a question for the user), so the
  // backend rejects it — catch it here first.
  const order = generateTimeSlots(preset.shift);
  const spans: { counter: string; s: number; e: number }[] = [];
  preset.pairs.forEach((pair, i) => {
    const s = order.indexOf(pair.start);
    const e = order.indexOf(pair.end);
    if (s < 0 || e < 0) return;
    [pair.from, pair.to].filter(Boolean).forEach((counter) => {
      if (spans.some((sp) => sp.counter === counter && s < sp.e && sp.s < e)) {
        out.push(`Pair ${i + 1}: ${counter} is already used over an overlapping time.`);
      }
      spans.push({ counter, s, e });
    });
  });

  return out;
}

interface QuickActionsSectionProps {
  /** The roster currently on screen, if any — decides which presets Apply can target. */
  operationMode?: string;
  shiftType?: string;
  generated?: boolean;
  /** Called after a preset is successfully applied, so the caller can refetch the roster. */
  onApplied?: () => void;
}

/** One preset mid-apply: resolved into counter numbers, and (once checked) its conflicts. */
interface ApplyState {
  index: number;
  ops: CounterOp[];
  conflicts: CounterOpConflict[] | null;
  checking: boolean;
  applying: boolean;
}

export function QuickActionsSection({
  operationMode,
  shiftType,
  generated,
  onApplied,
}: QuickActionsSectionProps) {
  const { notifySuccess, notifyError } = useNotification();
  const [presets, setPresets] = useState<QuickActionPreset[]>([]);
  const [counterDefsByMode, setCounterDefsByMode] = useState<Record<string, CounterDefinition[]>>({});
  const [saving, setSaving] = useState(false);
  const [applyState, setApplyState] = useState<ApplyState | null>(null);

  useEffect(() => {
    apiClient.adminConfig
      .getQuickActions()
      .then((cfg) => setPresets(cfg.presets ?? []))
      .catch(() => {});

    // Counter definitions come from the live template, so renaming a counter
    // there immediately narrows the dropdowns here, and Apply keeps resolving
    // labels to the right counter number even after a rename.
    MODES.forEach((mode) => {
      apiClient
        .getTemplateMeta(mode, "morning")
        .then((meta) =>
          setCounterDefsByMode((prev) => ({
            ...prev,
            [mode]: meta.counter_definitions,
          })),
        )
        .catch(() => {});
    });
  }, []);

  const countersByMode = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const [mode, defs] of Object.entries(counterDefsByMode)) out[mode] = defs.map((d) => d.label);
    return out;
  }, [counterDefsByMode]);

  const update = useCallback(
    (index: number, patch: Partial<QuickActionPreset>) =>
      setPresets((prev) => prev.map((p, i) => (i === index ? { ...p, ...patch } : p))),
    [],
  );

  const updatePair = useCallback(
    (pi: number, ci: number, patch: Partial<QuickActionPair>) =>
      setPresets((prev) =>
        prev.map((p, i) =>
          i !== pi ? p : { ...p, pairs: p.pairs.map((c, j) => (j === ci ? { ...c, ...patch } : c)) },
        ),
      ),
    [],
  );

  const addPreset = () => {
    const shift: ShiftType = "morning";
    const slots = generateTimeSlots(shift);
    setPresets((prev) => [
      ...prev,
      {
        name: `Preset ${prev.length + 1}`,
        action: "move",
        mode: "arrival",
        shift,
        pairs: [{ start: slots[16], end: slots[24], from: "", to: "" }],
      },
    ]);
  };

  const addPair = (pi: number) =>
    setPresets((prev) =>
      prev.map((p, i) => {
        if (i !== pi) return p;
        const last = p.pairs[p.pairs.length - 1];
        const slots = generateTimeSlots(p.shift);
        return {
          ...p,
          pairs: [...p.pairs, {
            start: last?.start ?? slots[16],
            end: last?.end ?? slots[24],
            from: "",
            to: "",
          }],
        };
      }),
    );

  const problems = useMemo(
    () => presets.map((p) => presetProblems(p, countersByMode[p.mode] ?? [])),
    [presets, countersByMode],
  );
  const hasProblems = problems.some((p) => p.length > 0);

  const handleSave = async () => {
    if (hasProblems) return;
    setSaving(true);
    try {
      await apiClient.adminConfig.setQuickActions({ presets });
      notifySuccess("Quick actions saved.");
    } catch {
      notifyError("Failed to save quick actions.");
    } finally {
      setSaving(false);
    }
  };

  /** Whether Apply can target the roster currently on screen with this preset. */
  const canApply = (preset: QuickActionPreset) =>
    Boolean(generated) && preset.mode === operationMode && preset.shift === shiftType;

  const startApply = async (pi: number) => {
    const preset = presets[pi];
    const labelToIndex = new Map(
      (counterDefsByMode[preset.mode] ?? []).map((d) => [d.label, d.index]),
    );
    const ops: CounterOp[] = preset.pairs
      .filter((p) => p.from && p.to && labelToIndex.has(p.from) && labelToIndex.has(p.to))
      .map((p) => ({
        action: preset.action,
        source: labelToIndex.get(p.from)!,
        target: labelToIndex.get(p.to)!,
        start_time: p.start,
        end_time: p.end,
      }));

    if (ops.length === 0) {
      notifyError("Nothing to apply — every pair in this preset is unusable.");
      return;
    }

    setApplyState({ index: pi, ops, conflicts: null, checking: true, applying: false });
    try {
      const { conflicts } = await apiClient.previewCounterOps({ ops });
      setApplyState((s) => (s && s.index === pi ? { ...s, conflicts, checking: false } : s));
    } catch {
      // The server is what actually catches a clash on write; a failed preview
      // just means Apply proceeds without the warning instead of blocking.
      setApplyState((s) => (s && s.index === pi ? { ...s, conflicts: [], checking: false } : s));
    }
  };

  const cancelApply = () => setApplyState(null);

  const confirmApply = async (onConflict?: ConflictChoice) => {
    if (!applyState) return;
    const preset = presets[applyState.index];
    setApplyState((s) => (s ? { ...s, applying: true } : s));
    try {
      await apiClient.batchCounterOps({
        ops: applyState.ops,
        ...(onConflict ? { on_conflict: onConflict } : {}),
        label: preset.name,
      });
      notifySuccess(`Applied "${preset.name}" to the current roster.`);
      setApplyState(null);
      onApplied?.();
    } catch (err) {
      notifyError(err instanceof Error ? err.message : "Could not apply this preset.");
      setApplyState((s) => (s ? { ...s, applying: false } : s));
    }
  };

  return (
    <>
      <div style={{ fontSize: 12, color: "#9ca3af", margin: "12px 0 10px", lineHeight: 1.6 }}>
        Batch move/swap. If it results in counters already occupied, you will be
        prompted whether to overwrite or allow multiple officers.
      </div>

      {presets.length === 0 && (
        <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 8 }}>
          No presets yet — add one below.
        </div>
      )}

      {presets.map((preset, pi) => {
        const counters = countersByMode[preset.mode] ?? [];
        const times = generateTimeSlots(preset.shift);
        const errs = problems[pi];

        return (
          <div
            key={pi}
            style={{
              border: `1px solid ${errs.length ? "#fca5a5" : "#e5e7eb"}`,
              borderRadius: 8,
              padding: 10,
              marginBottom: 10,
              background: errs.length ? "#fef2f2" : "white",
            }}
          >
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
              <input
                value={preset.name}
                onChange={(e) => update(pi, { name: e.target.value })}
                placeholder="Preset name"
                style={{ ...selStyle, flex: "1 1 140px", minWidth: 120, fontWeight: 600 }}
              />
              <select
                value={preset.action}
                onChange={(e) => update(pi, { action: e.target.value as "move" | "swap" })}
                style={selStyle}
              >
                <option value="move">move →</option>
                <option value="swap">swap ⇄</option>
              </select>
              <select
                value={preset.mode}
                onChange={(e) => update(pi, { mode: e.target.value as OperationMode })}
                style={selStyle}
              >
                {MODES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <select
                value={preset.shift}
                onChange={(e) => {
                  // Times are shift-specific, so re-anchor every pair onto the new grid.
                  const next = generateTimeSlots(e.target.value as ShiftType);
                  update(pi, {
                    shift: e.target.value as ShiftType,
                    pairs: preset.pairs.map((c) => ({ ...c, start: next[16], end: next[24] })),
                  });
                }}
                style={selStyle}
              >
                {SHIFTS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <button
                onClick={() => setPresets((prev) => prev.filter((_, i) => i !== pi))}
                style={iconBtn("#ef4444")}
                title="Delete this preset"
              >
                ✕ preset
              </button>
            </div>

            {/* Horizontal-scroll wrapper so columns stay readable on a phone
                instead of the fields wrapping onto their own lines. */}
            <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" as React.CSSProperties["WebkitOverflowScrolling"] }}>
              <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 420 }}>
                <thead>
                  <tr>
                    {["From", "", "To", "Start", "End", ""].map((h, i) => (
                      <th
                        key={i}
                        style={{
                          padding: "3px 4px",
                          fontSize: 10,
                          fontWeight: 600,
                          color: "#9ca3af",
                          textAlign: "left",
                          borderBottom: "1px solid #e5e7eb",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preset.pairs.map((pair, ci) => (
                    <tr key={ci}>
                      <td style={{ padding: "4px 4px 4px 0" }}>
                        <select
                          value={pair.from}
                          onChange={(e) => updatePair(pi, ci, { from: e.target.value })}
                          style={{
                            ...selStyle, width: "100%", minWidth: 88,
                            // Move: the counter officers leave is amber, the one they land on teal.
                            ...(preset.action === "move"
                              ? { background: SWAP_MOVE_COLORS.move.source.chip, borderColor: SWAP_MOVE_COLORS.move.source.border, color: SWAP_MOVE_COLORS.move.source.label }
                              : {}),
                          }}
                        >
                          <option value="">From…</option>
                          {counters.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: "4px", textAlign: "center", color: "#6b7280", fontWeight: 700 }}>
                        {preset.action === "swap" ? "⇄" : "→"}
                      </td>
                      <td style={{ padding: 4 }}>
                        <select
                          value={pair.to}
                          onChange={(e) => updatePair(pi, ci, { to: e.target.value })}
                          style={{
                            ...selStyle, width: "100%", minWidth: 88,
                            ...(preset.action === "move"
                              ? { background: SWAP_MOVE_COLORS.move.target.chip, borderColor: SWAP_MOVE_COLORS.move.target.border, color: SWAP_MOVE_COLORS.move.target.label }
                              : {}),
                          }}
                        >
                          <option value="">To…</option>
                          {counters.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: 4 }}>
                        <select
                          value={pair.start}
                          onChange={(e) => updatePair(pi, ci, { start: e.target.value })}
                          style={{ ...selStyle, width: "100%", minWidth: 74 }}
                        >
                          {times.slice(0, -1).map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: 4 }}>
                        <select
                          value={pair.end}
                          onChange={(e) => updatePair(pi, ci, { end: e.target.value })}
                          style={{ ...selStyle, width: "100%", minWidth: 74 }}
                        >
                          {times.slice(1).map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: "4px 0 4px 4px" }}>
                        <button
                          onClick={() =>
                            setPresets((prev) =>
                              prev.map((p, i) =>
                                i !== pi ? p : { ...p, pairs: p.pairs.filter((_, j) => j !== ci) },
                              ),
                            )
                          }
                          style={iconBtn("#fca5a5")}
                          title="Remove this pair"
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              onClick={() => addPair(pi)}
              style={{ ...iconBtn("#9ca3af"), borderStyle: "dashed", color: "#6b7280" }}
            >
              + pair
            </button>

            {errs.length > 0 && (
              <ul style={{ margin: "8px 0 0 16px", padding: 0, fontSize: 11, color: "#b91c1c" }}>
                {errs.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            )}

            {/* Apply — only meaningful against a roster of this preset's own
                mode/shift, so anything else explains why it's not offered. */}
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #f3f4f6" }}>
              {applyState?.index === pi ? (
                <div>
                  {applyState.checking && (
                    <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 8 }}>
                      Checking destinations…
                    </div>
                  )}
                  {applyState.conflicts && applyState.conflicts.length > 0 && (
                    <div
                      style={{
                        background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 7,
                        padding: "8px 10px", marginBottom: 8,
                      }}
                    >
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#991b1b", marginBottom: 4 }}>
                        ⚠️ Already has officers
                      </div>
                      {applyState.conflicts.map((c) => (
                        <div key={c.counter} style={{ fontSize: 11, color: "#7f1d1d", lineHeight: 1.5 }}>
                          <strong>{c.counter_label}</strong> — {c.officers.join(", ")}
                          <span style={{ color: "#991b1b" }}> ({c.slot_count} slot{c.slot_count === 1 ? "" : "s"})</span>
                        </div>
                      ))}
                      <div style={{ fontSize: 10, color: "#991b1b", marginTop: 6, lineHeight: 1.4 }}>
                        Overwrite unassigns them. Keep both leaves two officers on the
                        counter for you to sort out on the grid.
                      </div>
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button
                      onClick={cancelApply}
                      style={{
                        padding: "6px 14px", background: "#f1f5f9", border: "1px solid #e2e8f0",
                        borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#374151",
                      }}
                    >
                      Cancel
                    </button>
                    {applyState.conflicts && applyState.conflicts.length > 0 ? (
                      <>
                        <button
                          onClick={() => void confirmApply("keep_both")}
                          disabled={applyState.applying}
                          style={{
                            padding: "6px 12px", border: "1px solid #94a3b8", borderRadius: 6,
                            fontSize: 12, fontWeight: 700, color: "#334155", background: "#f8fafc",
                            cursor: applyState.applying ? "not-allowed" : "pointer",
                          }}
                        >
                          Keep both
                        </button>
                        <button
                          onClick={() => void confirmApply("overwrite")}
                          disabled={applyState.applying}
                          style={{
                            padding: "6px 12px", border: "none", borderRadius: 6,
                            fontSize: 12, fontWeight: 700, color: "white",
                            background: applyState.applying ? "#cbd5e1" : "#dc2626",
                            cursor: applyState.applying ? "not-allowed" : "pointer",
                          }}
                        >
                          Overwrite
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => void confirmApply()}
                        disabled={applyState.applying || applyState.checking}
                        style={{
                          padding: "6px 14px", border: "none", borderRadius: 6,
                          fontSize: 12, fontWeight: 700, color: "white",
                          background: applyState.applying || applyState.checking ? "#cbd5e1" : "#059669",
                          cursor: applyState.applying || applyState.checking ? "not-allowed" : "pointer",
                        }}
                      >
                        {applyState.applying ? "Applying…" : "Apply"}
                      </button>
                    )}
                  </div>
                </div>
              ) : canApply(preset) ? (
                <button
                  onClick={() => void startApply(pi)}
                  disabled={errs.length > 0}
                  title={errs.length > 0 ? "Fix the problems above first" : undefined}
                  style={{
                    padding: "5px 12px", borderRadius: 6, fontSize: 12, fontWeight: 700,
                    border: `1.5px solid ${errs.length > 0 ? "#d1d5db" : "#059669"}`,
                    color: errs.length > 0 ? "#9ca3af" : "#059669",
                    background: "white",
                    cursor: errs.length > 0 ? "not-allowed" : "pointer",
                  }}
                >
                  ▶ Apply to current roster
                </button>
              ) : (
                <div style={{ fontSize: 11, color: "#9ca3af" }}>
                  {!generated
                    ? "No roster generated yet."
                    : `Switch to a ${preset.mode} ${preset.shift} roster to apply this preset.`}
                </div>
              )}
            </div>
          </div>
        );
      })}

      <button
        onClick={addPreset}
        style={{
          padding: "4px 12px", background: "none", border: "1px dashed #9ca3af",
          borderRadius: 4, color: "#6b7280", cursor: "pointer", fontSize: 12,
        }}
      >
        + Add preset
      </button>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        <button
          onClick={handleSave}
          disabled={saving || hasProblems}
          title={hasProblems ? "Fix the problems above first" : undefined}
          style={{
            padding: "6px 16px",
            background: saving || hasProblems ? "#9ca3af" : "#667eea",
            color: "white", border: "none", borderRadius: 6,
            cursor: saving || hasProblems ? "not-allowed" : "pointer",
            fontSize: 13, fontWeight: 600,
          }}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </>
  );
}
